// src/layouts/configuration/components/pricingRules/index.js
import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
// --- Material-UI & Argon Components ---
import {
  Card,
  Collapse,
  IconButton,
  Icon,
  Paper,
  Divider,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Checkbox,
  Grid,
  TextField,
  InputAdornment,
  Pagination,
} from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';
import SearchIcon from '@mui/icons-material/Search';
import { grey } from '@mui/material/colors';
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import ArgonSelect from 'components/ArgonSelect';

// --- Contexts & Services ---
import { useVisibility } from 'context/visibilityContext';
import { useNotifications } from 'context/NotificationContext';
import { renderFields } from '../utils/fieldRenderer';

const PricingDataTable = ({ title, data, columns }) => {
  if (!data || data.length === 0 || !columns || columns.length === 0) {
    return null;
  }

  const getFieldValue = (item, field) => {
    if (field === 'SBQQ__Active__c' || field === 'SBQQ__ParentRuleIsActive__c') {
      const isActive = item.fields?.[field] === 'true' || item.fields?.[field] === true;
      return (
        <Tooltip title={isActive ? 'Active' : 'Inactive'}>
          {isActive ? (
            <CircleIcon color="success" sx={{ fontSize: 16, verticalAlign: 'middle' }} />
          ) : (
            <CircleIcon color="disabled" sx={{ fontSize: 16, verticalAlign: 'middle' }} />
          )}
        </Tooltip>
      );
    }

    const fields = item.fields || {};
    if (field.toLowerCase() === 'name' && item.name) return item.name;
    const value = fields[field];
    return value === null || value === undefined ? 'N/A' : String(value);
  };

  return (
    <ArgonBox mt={2}>
      <ArgonTypography variant="h6" fontWeight="medium" mb={1} ml={1}>
        {title}
      </ArgonTypography>
      <TableContainer component={Paper}>
        <Table size="small" aria-label={`${title.toLowerCase()} table`}>
          <TableHead sx={{ backgroundColor: grey[200], display: 'contents' }}>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col}>
                  <ArgonTypography variant="caption" fontWeight="bold">
                    {col
                      .replace(/SBQQ__/g, '')
                      .replace(/__c/g, '')
                      .replace(/([A-Z])/g, ' $1')
                      .trim()}
                  </ArgonTypography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item, index) => (
              <TableRow key={item.id || item.sfId || index}>
                {columns.map((col) => (
                  <TableCell key={col}>{getFieldValue(item, col)}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </ArgonBox>
  );
};

// --- MODIFICATION: Accept `orgType` prop ---
function PricingRules({ orgs, loading, priceRules, selectedOrgId, orgType, onItemSelect, selectedItems }) {
  const { visibilityConfig } = useVisibility();
  const { addNotification } = useNotifications();

  const [selectedOrg, setSelectedOrg] = useState(null);
  const [expandedRule, setExpandedRule] = useState({});
  const [selectedRules, setSelectedRules] = useState([]);
  
  // Search and pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const rowsPerPage = 8;

  // Sync with parent selected items
  React.useEffect(() => {
    setSelectedRules(selectedItems?.pricingRules || []);
  }, [selectedItems]);

  const handleRuleSelect = (rule, isSelected) => {
    let newSelection;
    if (isSelected) {
      newSelection = [...selectedRules, rule];
    } else {
      // Try both sfId and id for comparison
      newSelection = selectedRules.filter((r) => {
        const rSfId = r.sfId || r.id;
        const ruleSfId = rule.sfId || rule.id;
        return rSfId !== ruleSfId;
      });
    }
    setSelectedRules(newSelection);
    onItemSelect('pricingRules', newSelection);
  };

  const isRuleSelected = (rule) => {
    return selectedRules.some((r) => {
      const rSfId = r.sfId || r.id;
      const ruleSfId = rule.sfId || rule.id;
      return rSfId === ruleSfId;
    });
  };

  const orgOptions = useMemo(() => {
    if (!orgs) return [];
    return orgs.map((org) => ({
      label: org.name,
      value: org.instance_url,
      orgData: org,
    }));
  }, [orgs]);

  // --- MODIFICATION: Use `orgType` to select the correct config section ---
  const { priceRulesFields, priceConditionFields, priceActionFields, lookupQueryFields } = useMemo(() => {
    // `orgType` will be 'cpq' or 'rc'
    const configSection = visibilityConfig[orgType] || [];

    const findFields = (objectName) => {
      const config = configSection.find((obj) => obj.objectName === objectName);
      return config?.displayColumns
        ? config.displayColumns
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        : [];
    };
    return {
      priceRulesFields: findFields('SBQQ__PriceRule__c'),
      priceConditionFields: findFields('SBQQ__PriceCondition__c'),
      priceActionFields: findFields('SBQQ__PriceAction__c'),
      lookupQueryFields: findFields('SBQQ__LookupQuery__c'),
    };
  }, [visibilityConfig, orgType]); // Add orgType to dependency array
  // --- END MODIFICATION ---

  const handleChangeOrg = (event) => {
    const { value } = event.target;
    const selectedOrgOption = orgOptions.find((orgOpt) => orgOpt.value === value);
    setSelectedOrg(selectedOrgOption || null);
  };

  const handleRuleClick = (ruleId) => {
    setExpandedRule((prev) => ({ ...prev, [ruleId]: !prev[ruleId] }));
  };

  if (!selectedOrgId) {
    return (
      <Card>
        <ArgonBox p={3} textAlign="center">
          <ArgonTypography variant="body2" color="textSecondary">
            Please select an organization to view its pricing rules.
          </ArgonTypography>
        </ArgonBox>
      </Card>
    );
  }

  if (!loading && (!priceRules || priceRules.length === 0)) {
    return (
      <Card>
        <ArgonBox p={3} textAlign="center">
          <ArgonTypography variant="body2" color="textSecondary">
            No pricing rules found for the selected organization.
          </ArgonTypography>
        </ArgonBox>
      </Card>
    );
  }

  // Filter and paginate
  const filteredRules = priceRules.filter((rule) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return rule.name?.toLowerCase().includes(searchLower);
  });

  const pageCount = Math.ceil(filteredRules.length / rowsPerPage);
  const paginatedRules = filteredRules.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  return (
    <ArgonBox>
      {/* Search Bar */}
      <ArgonBox mb={2} display="flex" justifyContent="flex-end">
        <TextField
          size="medium"
          placeholder="Search pricing rules..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(0); // Reset to first page on search
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ maxWidth: '250px' }}
        />
      </ArgonBox>

      {/* Rules List */}
      {paginatedRules.length > 0 ? (
        <>
          {paginatedRules.map((rule) => {
            const ruleId = rule.id;
            const isSelected = isRuleSelected(rule);

            const priceConditionsData = rule.priceConditions?.map((c) => c.priceConditions).filter(Boolean) || [];
            const priceActionsData = rule.priceActions?.map((a) => a.priceActions).filter(Boolean) || [];
            const lookupQueriesData = rule.lookupQueries?.map((q) => q.lookupQuery).filter(Boolean) || [];

            return (
              <React.Fragment key={ruleId}>
                <Card sx={{ mb: 1 }}>
                  <ArgonBox
                    p={1}
                    borderRadius="md"
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    onClick={(e) => {
                      // Only handle row click if not clicking on checkbox or expand button
                      if (!e.target.closest('input[type="checkbox"]') && !e.target.closest('button')) {
                        handleRuleClick(ruleId);
                      }
                    }}
                    sx={{ cursor: 'pointer' }}
                    mt={0.5}
                    mb={0.5}
                  >
                    <ArgonBox display="flex" alignItems="center">
                      <Checkbox
                        checked={isSelected}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleRuleSelect(rule, e.target.checked);
                        }}
                        size="small"
                        sx={{ zIndex: 10, position: 'relative' }}
                      />
                      <ArgonTypography variant="h6" fontWeight="medium" color="secondary" ml={1}>
                        {rule.name}
                      </ArgonTypography>
                    </ArgonBox>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRuleClick(ruleId);
                      }}
                      sx={{ zIndex: 10, position: 'relative' }}
                    >
                      <Icon>{expandedRule[ruleId] ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
                    </IconButton>
                  </ArgonBox>
                  <Collapse in={expandedRule[ruleId]} timeout="auto" unmountOnExit>
                    <ArgonBox p={2}>
                      {renderFields(rule.fields, { allowedFields: priceRulesFields })}
                      <Divider sx={{ my: 2 }} />

                      <PricingDataTable
                        title="Price Conditions"
                        data={priceConditionsData}
                        columns={priceConditionFields}
                      />
                      <PricingDataTable title="Price Actions" data={priceActionsData} columns={priceActionFields} />
                      <PricingDataTable title="Lookup Queries" data={lookupQueriesData} columns={lookupQueryFields} />
                    </ArgonBox>
                  </Collapse>
                </Card>
              </React.Fragment>
            );
          })}

          {/* Pagination */}
          {pageCount > 1 && (
            <ArgonBox display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={pageCount}
                page={page + 1}
                onChange={(e, newPage) => setPage(newPage - 1)}
                color="primary"
              />
            </ArgonBox>
          )}
        </>
      ) : (
        <ArgonBox p={3} textAlign="center">
          <ArgonTypography variant="body2" color="textSecondary">
            {searchTerm ? 'No pricing rules match your search.' : 'No pricing rules found for the selected organization.'}
          </ArgonTypography>
        </ArgonBox>
      )}
    </ArgonBox>
  );
}

// --- MODIFICATION: Add `orgType` to propTypes ---
PricingRules.propTypes = {
  orgs: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  priceRules: PropTypes.array,
  selectedOrgId: PropTypes.string,
  orgType: PropTypes.string,
};

PricingRules.defaultProps = {
  priceRules: [],
  selectedOrgId: '',
  orgType: 'cpq', // Default value
};
// --- END MODIFICATION ---

export default PricingRules;
